import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { NbButton, NbInput, NbTextarea } from "../components/nb";
import { Truck, CreditCard, Lock, Check, QrCode } from "lucide-react";

export default function OrderCards() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ card_number: "", exp: "", cvv: "", name_on_card: "", shipping_address: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/order-cards", form);
      setDone(data.payment);
      toast.success("Order placed! Your cards are on the way 🎉");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#FCFCFA]"><Navbar />
        <div className="max-w-md mx-auto px-4 py-16 text-center">
          <div className="bg-white rounded-3xl border border-stone-100 shadow-[0_16px_50px_rgb(28,25,23,0.08)] p-10">
            <div className="w-14 h-14 rounded-full bg-[#F3E8E6] flex items-center justify-center mx-auto mb-4"><Check className="w-7 h-7 text-[#8B1E3F]" strokeWidth={3} /></div>
            <h1 className="font-display text-3xl font-medium mb-2 text-stone-900">Order confirmed</h1>
            <p className="text-stone-600">100 printed QR cards · <span className="font-medium text-stone-900">$15.00</span></p>
            <p className="text-xs text-stone-400 mt-1">Txn {done.transaction_id} · ****{done.card_last4}</p>
            <div className="flex flex-col gap-2 mt-6">
              <NbButton onClick={() => navigate("/dashboard")}>Back to Dashboard</NbButton>
              <NbButton as={Link} to="/settings" variant="outline">View Payment History</NbButton>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FCFCFA]">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 sm:px-8 py-10">
        <h1 className="font-display text-4xl font-medium tracking-tight text-stone-900 mb-1 flex items-center gap-2"><Truck className="w-7 h-7 text-[#D4AF37]" /> Order Printed Cards</h1>
        <p className="text-stone-600 mb-8">Hand out your QR in person. 100 premium cards, printed with your code & name.</p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-[#1C1917] text-white rounded-2xl p-7 h-fit relative overflow-hidden">
            <div className="absolute inset-0 opacity-60" style={{ background: "radial-gradient(120% 120% at 100% 0%, rgba(212,175,55,0.18) 0%, transparent 45%)" }} />
            <div className="relative">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 font-display text-xl font-medium"><QrCode className="w-5 h-5 text-[#D4AF37]" /> 100 QR Cards</div>
                <span className="font-display text-3xl font-medium">$15</span>
              </div>
              <p className="text-sm text-white/50 mt-1">One-time order · 15¢ a card</p>
              <ul className="mt-5 space-y-2.5">
                {["Your personal QR + name printed", "Premium matte finish", "Designed in your badge theme", "Ships to your door"].map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-white/85"><Check className="w-4 h-4 text-[#D4AF37]" strokeWidth={3} /> {f}</li>
                ))}
              </ul>
              <div className="mt-6 pt-5 border-t border-white/10 flex items-center justify-between">
                <span className="text-white/70">Total due today</span>
                <span className="font-display text-2xl font-medium">$15.00</span>
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="bg-white rounded-2xl border border-stone-100 shadow-[0_8px_30px_rgb(28,25,23,0.05)] p-7 space-y-4 h-fit">
            <div className="flex items-center gap-2 font-display text-xl font-medium text-stone-900"><CreditCard className="w-5 h-5" /> Checkout</div>
            <div className="bg-stone-50 border border-stone-200 rounded-xl p-2.5 text-xs text-stone-500 flex items-center gap-1.5"><Lock className="w-3 h-3" /> Secured by Authorize.Net (sandbox)</div>
            <div><label className="text-sm font-medium text-stone-700">Shipping Address</label><NbTextarea data-testid="ship-address" rows={2} required value={form.shipping_address} onChange={(e) => setForm({ ...form, shipping_address: e.target.value })} className="mt-1.5" placeholder="Street, City, State, ZIP" /></div>
            <div><label className="text-sm font-medium text-stone-700">Name on Card</label><NbInput data-testid="card-name" required value={form.name_on_card} onChange={(e) => setForm({ ...form, name_on_card: e.target.value })} className="mt-1.5" /></div>
            <div><label className="text-sm font-medium text-stone-700">Card Number</label><NbInput data-testid="card-number" required value={form.card_number} onChange={(e) => setForm({ ...form, card_number: e.target.value })} className="mt-1.5" placeholder="4111 1111 1111 1111" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-stone-700">Expiry</label><NbInput data-testid="card-exp" required value={form.exp} onChange={(e) => setForm({ ...form, exp: e.target.value })} className="mt-1.5" placeholder="MM/YY" /></div>
              <div><label className="text-sm font-medium text-stone-700">CVV</label><NbInput data-testid="card-cvv" required value={form.cvv} onChange={(e) => setForm({ ...form, cvv: e.target.value })} className="mt-1.5" placeholder="123" /></div>
            </div>
            <NbButton type="submit" disabled={loading} className="w-full" data-testid="order-submit">{loading ? "Processing..." : "Pay $15.00"}</NbButton>
            <p className="text-xs text-stone-400 text-center">Test card: 4111 1111 1111 1111</p>
          </form>
        </div>
      </div>
    </div>
  );
}
